<template lang="pug">
include /../../s-pug/mixin-icon
.EditorContainer._h100(:class="{'is-active': isEditorFocused}")
   editor-menu-bar(ref="jsEditorFloatingMenu" :editor="editor" v-slot="{ commands, isActive, menu }")
      ._bg-grey-050.Editor-toolBarContainer._sticky._t0._z50
         ._flex._p025
            button.Editor-menuButton._mr025._ff-monospace(v-if="!isActive.image() && allowAction('heading')" :class="{ 'is-active': isActive.heading({ level: 3 }) }" @click.stop.prevent="commands.heading({ level: 3 })"): ._flex-alignSelfCenter H
            button.Editor-menuButton._mr025._ff-monospace(v-if="!isActive.image() && allowAction('bold')" :class="{ 'is-active': isActive.bold() }" @click.stop.prevent="commands.bold"): ._flex-alignSelfCenter B
            button.Editor-menuButton._mr025._ff-monospace(v-if="!isActive.image() && allowAction('italic')" :class="{ 'is-active': isActive.italic() }" @click.stop.prevent="commands.italic"): ._flex-alignSelfCenter I
            button.Editor-menuButton._mr1-5._hasSvgChild--noStroke._svg-noPrimarySecondaryDifference(v-if="!isActive.image() && allowAction('link')" :class="{ 'is-active': isActive.link() }" @click.stop.prevent="showLinkMenu(editor.getMarkAttrs('link'))"): +icon({name: 'icon-link'})
            //- 
            button.Editor-menuButton._mr05._hasSvgChild--noStroke(v-if="allowAction('bullet_list')" @click.stop.prevent="commands.bullet_list" title="insert bulleted list"): +icon({name: 'icon-ul'})
            button.Editor-menuButton._mr05._hasSvgChild--noStroke(v-if="allowAction('ordered_list')" @click.stop.prevent="commands.ordered_list" title="insert numbered list"): +icon({name: 'icon-ol'})
            button.Editor-menuButton._mr05._hasSvgChild--noFill(v-if="allowAction('image')" @click.stop.prevent="commands.image" title="insert image"): +icon({name: 'icon-image'})
            button.Editor-menuButton._mr05._hasSvgChild--noFill(v-if="allowAction('video')" @click.stop.prevent="commands.video" title="insert video embed"): +icon({name: 'icon-video'})
            //- Preview (maybe)
            button._ml-auto._pr05(@click.stop.prevent="showJobViewerHandler({overrideId: openingIdForPreview, isPreviewMode: true, overrideOpening: openingForJobViewer})" title="preview" v-if="showPreviewButton") Preview
         ._p05._t100._cursor-default._borderTop-default(v-if="linkMenuIsActive")
            form(@submit.prevent="setLinkUrl(commands, linkUrl)")
               ._mb05._flex
                  input.Input.Input--sm._fs-3(ref="linkInput" type="text" id="linkUrl" v-model.trim="linkUrl" placeholder="Full URL" @change="logChange")
                  button._ml05.Btn.Btn--red.Btn--xs._p025(@click.stop.prevent="removeLink(commands)" style="width: 30px; height: 30px" type="button")
                     +icon({grow: true, name: "icon-x"})._c-grey-800
               ._flex
                  a._ml-auto.Btn.Btn--yellow.Btn--sm._mr1(:href="linkUrl || editor.getMarkAttrs('link').href" target="_blank") Preview
                  button.Btn.Btn--sm.Btn--greyDk._mr05(type="button" @click.stop.prevent="cancelLinkOverlay()") Cancel
                  button.Btn.Btn--sm.Btn--green._px3(type="submit" :disabled="$v.linkUrl.$invalid") Done
   editor-content._h100.Editor(:editor="editor")
   //- Add content btn
   ._mt1-5._radius._bg-grey-050._boxShadow_hover._py1._flex._flex-alignItemsCenter._flex-justifyContentCenter._cursor-pointer(v-if="shouldShowAddContentBtn()" @click.stop.prevent="handleReplaceWithNodeClick()")
      .Editor-AddContentIcon: +icon({name: 'icon-add-circle'})
      ._ml1._c-grey-500._ls-1-5._fs-2 Add content
</template>

<style lang="sass">
.EditorContainer.is-active
   border-color: $light-blue-vivid-700
   box-shadow: 0 0 0 4px rgba($light-blue-vivid-600,0.15)
   outline: none
   border-radius: $radius
      
.ProseMirror
   height: 100%
   padding: 7px
   height: 100%
   border: solid 1px $grey-200
   border-radius: 0 0 $radius $radius
   border-top: none
   .is-active &
      outline: none

.Editor-toolBarContainer 
   border: solid 1px $grey-200
   border-radius: $radius $radius 0 0

.Editor-menuButton
   justify-content: center
   background: transparent
   border: 0
   color: $grey-800
   border-radius: $radius
   padding: 0
   cursor: pointer
   font-size: 18px
   width: 34px
   height: 34px
   display: flex
   position: relative
   svg
      align-self: center
      fill: $grey-700
      width: 22px
      height: auto
      stroke: currentColor
      +absoluteCenterXY()
   &:hover
      background-color: $grey-100
   &.is-active
      background-color: $grey-200

.Editor-imageWrap,
.Editor-embedWrap
   padding: $s-05
   border-radius: $radius
   background-color: $grey-050
   border: solid 1px $grey-100
   cursor: ns-resize
   margin-top: $s-2
.Editor-imageMetaSection
   +px($s-025)
   padding-top: $s-05
.Editor-helpIcon svg
   width: 16px
   .primary
      fill: $green-100
   .secondary
      fill: $green-700

.Editor p.is-empty
   // TODO: Empty `p` tags have a `br` and this is required by prosemirror (see: https://github.com/scrumpy/tiptap/issues/412)
   // When double clicking on this it selects, which causes the tooltip (for element inserting) to close.

   // Placeholder
   // &.is-focused
   &::before
      float: left
      color: $grey-500
      pointer-events: none
      height: 0
      font-style: italic
      // font-size: 80%
   &::before
      // pointer-events: none !important
      content: attr(data-empty-text)
   &.is-editorEmpty::before
      content: 'No content yet... click and begin adding'

.Editor-AddContentIcon svg
   +wh(24px)
   display: inherit
   .primary
      fill: $grey-100
   .secondary
      fill: $light-blue-vivid-800

</style>

<script>
import { Editor, EditorContent, EditorMenuBubble, EditorMenuBar, EditorFloatingMenu, Extension, Node, Text, Doc } from 'tiptap'
import getTipTapEditorInstance from '~/lib/getTipTapEditorInstance'
import { url } from 'vuelidate/lib/validators'

export default {
   props: ['value', 'allowedActions', 'showPreviewButton', 'openingIdForPreview', 'showJobViewerHandler', 'openingForJobViewer'],
   components: {
      EditorContent,
      EditorFloatingMenu,
      EditorMenuBubble,
      EditorMenuBar,
   },
   data() {
      return {
         isEditorFocused: false,
         editorChange: false,
         editor: null,
         linkUrl: null,
         linkMenuIsActive: false,
         menuFloatingIsActive: false,
         menuBubbleIsActive: false,
      }
   },
   validations: {
      linkUrl: {
         url,
      },
   },
   methods: {
      // To access `command` here:
      // var command = this.$refs.jsEditorFloatingMenu.editor.commands.image
      // Then use like this:
      // `command({src: file.nameWithHash})`
      logChange(event) {
         console.log(event)
         console.log(this.$v)
      },
      getDoc() {
         const state = this.editor && this.editor.state
         return state && state.doc
      },
      getLastChild() {
         const doc = this.getDoc()
         return doc && doc.lastChild
      },
      shouldShowAddContentBtn() {
         const lastChild = this.getLastChild()

         // const editorIsEmpty = this.value && this.value.content && this.value.content.length === 1 && !this.value.content[0].hasOwnProperty('content')

         // prettier-ignore
         const lastNodeWarrantsButton =
            lastChild &&
            lastChild.type.name !== 'paragraph' &&
            lastChild.type.name !== 'ordered_list' &&
            lastChild.type.name !== 'bullet_list'
         // ||
         // editorIsEmpty
         return lastNodeWarrantsButton
      },
      handleReplaceWithNodeClick() {
         this.editor.commands.replaceWithNode()
      },
      allowAction(actionString) {
         return this.allowedActions.includes(actionString)
      },
      showLinkMenu(attrs) {
         this.linkUrl = attrs.href
         this.linkMenuIsActive = true
         console.log('this.linkMenuIsActive: ', this.linkMenuIsActive)
         this.$nextTick(() => this.$refs.linkInput.focus())
      },
      cancelLinkOverlay() {
         this.hideLinkMenu()
      },
      hideLinkMenu() {
         // this.linkUrl = null
         this.linkMenuIsActive = false
      },
      setLinkUrl(commands, url) {
         commands.link({ href: url })
         this.hideLinkMenu()
      },
      removeLink(commands) {
         commands.link({ href: null })
         this.hideLinkMenu()
      },
      menuFloatingShowHandler() {
         this.menuFloatingIsActive = true
      },
      menuFloatingHideHandler() {
         this.hideLinkMenu()
         this.menuFloatingIsActive = false
      },
      menuBubbleShowHandler() {
         this.menuBubbleIsActive = true
      },
      menuBubbleHideHandler() {
         this.hideLinkMenu()
         this.menuBubbleIsActive = false
      },
   },
   mounted() {
      // See docs here (better than GitHub): https://tiptap.scrumpy.io/docs/api/classes.html#editor-options
      this.editor = getTipTapEditorInstance({
         args: {
            allowedActions: this.allowedActions,
            dropCursor: {
               color: '#40C3F7',
            },
            editorProps: {
               handlePaste: () => {
                  console.log(`Some new content was added from the user's clipboard`)
               },
               handleKeyDown: (view, event) => {
                  console.log(view)
                  console.log(event)
                  if (event.metaKey && event.key === 'k') {
                     this.showLinkMenu(this.editor.getMarkAttrs('link'))
                  }
               },
            },
            onUpdate: ({ getJSON, getHTML }) => {
               // console.log('editorState: ', this.editor.state)
               this.editorChange = true

               // Update `v-model` value
               // https://github.com/scrumpy/tiptap/issues/133#issuecomment-447555060
               this.$emit('input', { json: this.editor.getJSON(), html: this.editor.getHTML() })
            },
            onFocus: () => {
               this.isEditorFocused = true
            },
            onBlur: () => {
               this.isEditorFocused = false

               const doc = this.getDoc()
               if (!doc || this.menuFloatingIsActive) return

               let tr = this.editor.state.tr
               
               this.editor.view.dispatch(tr)
            },
         },
      })
      console.log('this.value: ', this.value)
      if (this.value) {
         this.editor.setContent(this.value.json)
      }
   },
   beforeDestroy() {
      this.editor.destroy()
   },
}
</script>
